import { useState } from 'react';
import { cn } from '@/lib/utils';

/**
 * Which shared colour a card's current-point dot wears. The status pills'
 * own foregrounds, so a sparkline and a pill saying the same thing match - and
 * dark mode comes with them:
 *
 * - `licensing` amber - licensing and SLA: expiries, unactivated seats, issues.
 * - `crash` red - crashes, and nothing else.
 * - `healthy` green - the number going up is good news.
 * - `neutral` slate - a count with no good or bad direction: labs, seats sold.
 */
export type SparkTone = 'licensing' | 'crash' | 'healthy' | 'neutral';

const DOT: Record<SparkTone, string> = {
  licensing: 'bg-pill-caution-foreground',
  crash: 'bg-pill-negative-foreground',
  healthy: 'bg-pill-positive-foreground',
  neutral: 'bg-pill-inert-foreground',
};

/** Vertical room inside the plot, so a dot at the top or bottom is not clipped. */
const INSET = 12;

/** "now", "1 wk ago", "8 wk ago" - relative, so rendering needs no clock. */
function ago(stepsBack: number, stepDays: number): string {
  if (stepsBack === 0) return 'now';
  const days = stepsBack * stepDays;
  return days % 7 === 0 ? `${String(days / 7)} wk ago` : `${String(days)} d ago`;
}

/**
 * A card's 9-point trend: a thin muted line, the latest point as a dot in the
 * card's shared colour, a tooltip on hover.
 *
 * The line is SVG stretched to the card's width; the dots are HTML positioned
 * by percentage on top of it, so they stay round however wide the card is.
 * The line is deliberately de-emphasised - the card's number is the headline,
 * the trend is context for it.
 */
export function Sparkline({
  points,
  stepDays,
  tone,
  label,
  surface = 'card',
  className,
}: {
  points: readonly number[];
  stepDays: number;
  tone: SparkTone;
  /** What is counted, for the accessible name: "Crashes". */
  label: string;
  /** What the dots are ringed in. `white` for the always-white tiles on the
   *  Critical banner, which do not follow the theme's card colour. */
  surface?: 'card' | 'white';
  className?: string;
}) {
  const [hover, setHover] = useState<number | null>(null);
  if (points.length < 2) return null;

  const min = Math.min(...points);
  const max = Math.max(...points);
  const span = max - min;
  /** 0 at the top, 100 at the bottom, in percent of the plot's height. A flat
   *  series sits in the middle rather than on the floor. */
  const y = (v: number) => (span === 0 ? 50 : INSET + (1 - (v - min) / span) * (100 - 2 * INSET));
  const x = (i: number) => (i / (points.length - 1)) * 100;

  const last = points.length - 1;
  const shown = hover ?? last;
  const ring = surface === 'white' ? 'ring-white' : 'ring-card';

  return (
    <div
      className={cn('relative h-8 w-full', className)}
      role="img"
      aria-label={`${label}, last ${String(points.length)} weeks: ${points.join(', ')}`}
      // The nearest point to the cursor, so the whole width is a hit target
      // and every mark is reachable however thin the line is.
      onMouseMove={(event) => {
        const box = event.currentTarget.getBoundingClientRect();
        const ratio = (event.clientX - box.left) / box.width;
        setHover(Math.min(last, Math.max(0, Math.round(ratio * last))));
      }}
      onMouseLeave={() => {
        setHover(null);
      }}
    >
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        className="absolute inset-0 size-full overflow-visible text-muted-foreground/45"
        aria-hidden="true"
      >
        <polyline
          points={points.map((v, i) => `${String(x(i))},${String(y(v))}`).join(' ')}
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>

      {/* The current point in the card's colour, ringed in the card surface so
          it lifts off the line under it. */}
      <span
        className={cn(
          `pointer-events-none absolute size-2 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ${ring}`,
          DOT[tone],
        )}
        style={{ left: `${String(x(last))}%`, top: `${String(y(points[last]!))}%` }}
        aria-hidden="true"
      />

      {hover !== null && hover !== last && (
        <span
          className={`pointer-events-none absolute size-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-muted-foreground ring-2 ${ring}`}
          style={{ left: `${String(x(hover))}%`, top: `${String(y(points[hover]!))}%` }}
          aria-hidden="true"
        />
      )}

      {hover !== null && (
        <span
          className="pointer-events-none absolute -top-1 z-10 -translate-x-1/2 -translate-y-full rounded-md border border-border bg-popover px-2 py-1 text-[11px] whitespace-nowrap text-popover-foreground shadow-md"
          style={{ left: `${String(Math.min(Math.max(x(shown), 15), 85))}%` }}
        >
          <span className="font-semibold">{points[shown]}</span>
          <span className="text-muted-foreground"> · {ago(last - shown, stepDays)}</span>
        </span>
      )}
    </div>
  );
}
